import React, { useMemo } from 'react';
import styles from './PasswordStrengthIndicator.module.css';

interface PasswordStrengthIndicatorProps {
  password: string;
}

type StrengthLevel = 'weak' | 'fair' | 'good' | 'strong';

interface StrengthResult {
  level: StrengthLevel;
  score: number;
  feedback: string[];
}

const calculatePasswordStrength = (password: string): StrengthResult => {
  let score = 0;
  const feedback: string[] = [];

  if (!password) {
    return {
      level: 'weak',
      score: 0,
      feedback: ['Enter a password'],
    };
  }

  // Length check
  if (password.length >= 8) {
    score += 1;
  } else {
    feedback.push('At least 8 characters');
  }

  if (password.length >= 12) {
    score += 1;
  }

  // Lowercase letters
  if (/[a-z]/.test(password)) {
    score += 1;
  } else {
    feedback.push('Add lowercase letters');
  }

  // Uppercase letters
  if (/[A-Z]/.test(password)) {
    score += 1;
  } else {
    feedback.push('Add uppercase letters');
  }

  // Numbers
  if (/\d/.test(password)) {
    score += 1;
  } else {
    feedback.push('Add numbers');
  }

  // Special characters
  if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
    score += 1;
  } else {
    feedback.push('Add special characters');
  }

  // Common patterns to avoid
  const commonPatterns = [
    /^[a-zA-Z]+$/, // Only letters
    /^\d+$/, // Only numbers
    /^(.)\1+$/, // Repeated characters
    /^(012|123|234|345|456|567|678|789|890|abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz)/i, // Sequential characters
  ];

  const hasCommonPattern = commonPatterns.some((pattern) =>
    pattern.test(password)
  );
  if (hasCommonPattern) {
    score = Math.max(0, score - 1);
    if (!feedback.includes('Avoid common patterns')) {
      feedback.push('Avoid common patterns');
    }
  }

  // Determine strength level
  let level: StrengthLevel;
  if (score <= 2) {
    level = 'weak';
  } else if (score <= 3) {
    level = 'fair';
  } else if (score <= 5) {
    level = 'good';
  } else {
    level = 'strong';
  }

  // Provide positive feedback for strong passwords
  if (level === 'strong') {
    feedback.length = 0;
    feedback.push('Excellent password!');
  } else if (level === 'good') {
    if (feedback.length === 0) {
      feedback.push('Good password');
    }
  }

  return {
    level,
    score: Math.min(score, 6), // Cap at 6 for percentage calculation
    feedback: feedback.slice(0, 3), // Limit feedback to 3 items
  };
};

export const PasswordStrengthIndicator: React.FC<
  PasswordStrengthIndicatorProps
> = ({ password }) => {
  const strength = useMemo(
    () => calculatePasswordStrength(password),
    [password]
  );

  const strengthPercentage = (strength.score / 6) * 100;

  const strengthLabels: Record<StrengthLevel, string> = {
    weak: 'Weak',
    fair: 'Fair',
    good: 'Good',
    strong: 'Strong',
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.label}>Password Strength:</span>
        <span className={`${styles.strengthLabel} ${styles[strength.level]}`}>
          {strengthLabels[strength.level]}
        </span>
      </div>

      <div className={styles.progressBar}>
        <div
          className={`${styles.progressFill} ${styles[strength.level]}`}
          style={{ width: `${strengthPercentage}%` }}
          role="progressbar"
          aria-valuenow={strength.score}
          aria-valuemin={0}
          aria-valuemax={6}
          aria-label={`Password strength: ${strengthLabels[strength.level]}`}
        />
      </div>

      {strength.feedback.length > 0 && (
        <ul className={styles.feedback}>
          {strength.feedback.map((item, index) => (
            <li key={index} className={styles.feedbackItem}>
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
